import React, {useEffect, useRef, useState} from 'react'
import { createPortal } from 'react-dom';
import { Localizer } from 'infrastructure/localization/localizer'
import { useHistory, useRouteMatch } from "react-router-dom";
import { ValidationForm } from 'core/components/validation/components/validationForm';
import { ModalSubmitMessage, ModalAcceptType } from 'core/components/modalMessage/modalSubmitMessage';
import useEffectAsync from 'infrastructure/effect';
import { HttpClientService, HttpResponseError } from 'services/httpClient/httpClientService';
import { AccountService } from 'services/account/accountService';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setSlideinState, SlideInStateEnum } from 'core/components/slideIn/slideInSlice';
import { ValidationFormSubmitButton } from 'core/components/validation/components/validationFormButton';
import { HandleError } from 'core/infrastructure/errors/errorBoundary';
import { BuildNew, CrudForloebInputModel, crudForloebValidationSchema, crudForloebWarningSchema } from './crudForloebInputModel';
import { ForloebApi } from "../../../services/api/forloeb/forloebApi";
import { forloebTableReducer, ForloebTableState } from 'pages/forloebList/forloebTableSlice';
import {ForloebModel} from "../../sharedmodels/forloeb/forloebModel";
import {ForloebCrudFields} from "./forloebCrudFields";
import {NotificationModule} from "ditmer-embla";
import {forloebReducer, ForloebSliceState, pingRefreshForloeb, setCurrentForloeb} from "../../forloebSlice";
import {ValidationTextarea} from "../../components/validation/components/validationTextarea";
import {nameof} from "ts-simple-nameof";
import { RoutePaths } from 'infrastructure/routes';
import { ForloebOverviewTypeEnum } from 'core/sharedmodels/forloeb/forloebOverviewTypeEnum';
import { ForloebOverviewLocationState } from 'core/components/forloebOverview/hooks/useForloebOverviewViewOptions';

const forloebApi = new ForloebApi(new HttpClientService(), new AccountService())

let pendingErrorModal: { show: boolean; message: string; modalId: string } = { show: false, message: "", modalId: "" };

const isPlannedForloebExistsError = (err: unknown): boolean => {
    return err instanceof HttpResponseError && 
           !!err.message && 
           err.message.toLowerCase().includes("planlagt forløb");
}

export function CreateForloeb(props: { modalId: string }) {
    const routeMatch = useRouteMatch();
    const history = useHistory();
    const forloebTableState = useAppSelector(forloebTableReducer) as ForloebTableState;
    const dispatch = useAppDispatch();
    const _currentUser = useRef(new AccountService().getUser());
    const [createForloebFailed, setCreateForloebFailed] = useState(false);
    const [, forceUpdate] = useState(0);
    const errorModalId = `${props.modalId}-planned-forloeb-exists`;
    const uddannelseslaegeId = (routeMatch.params as { id: string }).id;
    const [forloebListState, setForloebListState] = useState<ForloebModel[]>([]);
    const forloebSliceState = useAppSelector(forloebReducer) as ForloebSliceState;

    useEffect(() => {
        if (pendingErrorModal.show && pendingErrorModal.modalId === errorModalId) {
            $(`#${errorModalId}`).modal('show');
        }
    }, [errorModalId]);

    const handlePlannedForloebExistsError = (errorMessage: string) => {
        pendingErrorModal = { show: true, message: errorMessage, modalId: errorModalId };
        forceUpdate(n => n + 1);
        setTimeout(() => $(`#${errorModalId}`).modal('show'), 50);
    };

    const handleModalAccept = () => {
        pendingErrorModal = { show: false, message: "", modalId: "" };
        
        $(`#${errorModalId}`).modal('hide');
        
        const $slideIn = $(`#${props.modalId}`);
        
        $slideIn.one('hidden.bs.modal', () => {
            $('.modal-backdrop').remove();
            $('body').removeClass('modal-open');
            
            const state: ForloebOverviewLocationState = { viewType: ForloebOverviewTypeEnum.ForloebOverblikKladdeView };
            history.push(RoutePaths.forloebOverview.url, state);
        });
        
        dispatch(setSlideinState({ state: SlideInStateEnum.Closing, slideInId: props.modalId }));
    };

    const handleModalCancel = () => {
        pendingErrorModal = { show: false, message: "", modalId: "" };
    };

    const createForloeb = async (inputModel: CrudForloebInputModel) => {
        dispatch(setSlideinState({ state: SlideInStateEnum.ActionOngoing, slideInId: props.modalId }));

        const newForloeb = inputModel.forloebKladde
            ? await forloebApi.createForloebKladde(inputModel)
            : await forloebApi.createForloeb(inputModel);


        if (newForloeb) {
            setCreateForloebFailed(false);
            dispatch(setSlideinState({ state: SlideInStateEnum.Closing, slideInId: props.modalId }))
            inputModel.forloebKladde
                ? NotificationModule.showSuccess(Localizer.forloebOverviewPage_createdForloebKladde(), "")
                : NotificationModule.showSuccess(Localizer.forloebOverviewPage_createdForloeb(), "");
        }

        if (uddannelseslaegeId) {
            const forloebList = await forloebApi.getForloebByBruger(uddannelseslaegeId);
            if (forloebSliceState.forloebState.id !== undefined && !forloebList.some(f => f.id === forloebSliceState.forloebState.id)) {
                const ikkeFravaerForloebModel = forloebList.filter(f => !f.erFravaer)[0];
                if (ikkeFravaerForloebModel) {
                    dispatch(setCurrentForloeb(ikkeFravaerForloebModel.ToState()))
                    dispatch(pingRefreshForloeb())
                }
            }
        }
    }

    useEffectAsync(async () => {
        if (uddannelseslaegeId)
            setForloebListState(await forloebApi.getForloebByBruger(uddannelseslaegeId));
    }, [uddannelseslaegeId, forloebTableState])

    const render = (forloebListState &&
        <ValidationForm
            key={forloebListState.map(f => f.id).join()}
            initialValues={BuildNew(uddannelseslaegeId, forloebListState)}
            validationSchema={crudForloebValidationSchema}
            warningSchema={crudForloebWarningSchema}
            onSubmit={async (values) => {
                await createForloeb(values)
                    .catch(err => {
                        if (isPlannedForloebExistsError(err)) {
                            handlePlannedForloebExistsError(err.message);
                            setCreateForloebFailed(false);
                        } else {
                            HandleError(err);
                            setCreateForloebFailed(true);
                        }
                        dispatch(setSlideinState({ state: SlideInStateEnum.Opened, slideInId: props.modalId }))
                    });
            }}>
            {
                (formik) => (
                    <>
                        <ForloebCrudFields modalId={props.modalId} formik={formik} isKladde/>
                        {(_currentUser.current.IsAdminOrVus() ||_currentUser.current.IsHrMedarbejder()) &&
                            <div className="row">
                                <div className="col-12">
                                    <ValidationTextarea
                                        model={{
                                            label: Localizer.forloebpage_BemaerkningerTilForloeb(),
                                            placeholder: Localizer.forloebpage_BemaerkningerTilForloebLabel(),
                                            htmlName: nameof<CrudForloebInputModel>(x => x.infoTekst)
                                        }}
                                        inputId={nameof<CrudForloebInputModel>(x => x.infoTekst)}
                                        formikProps={formik}
                                    />
                                </div>
                            </div>
                        }
                        <div className="modal-actions">
                            {createForloebFailed &&
                                <div className="form-group margin-bottom-m">
                                    <div className="field-validation-error">{Localizer.forloebpage_createFailed()}</div>
                                </div>
                            }
                            <ValidationFormSubmitButton formikProps={formik}>
                                {Localizer.forloebpage_createForloeb()}
                            </ValidationFormSubmitButton>
                        </div>
                    </>
                )
            }
        </ValidationForm>
    )

    return (
        <>
            {render}
            {createPortal(
                <ModalSubmitMessage
                    modalId={errorModalId}
                    title={Localizer.global_error()}
                    description={<p>{pendingErrorModal.message}</p>}
                    modalAcceptType={ModalAcceptType.primary}
                    acceptButtonText={Localizer.forloebpage_goToPlanlagteForloeb()}
                    cancelButtonText={Localizer.global_cancel()}
                    acceptAction={handleModalAccept}
                    cancelAction={handleModalCancel}
                />,
                document.body
            )}
        </>
    );
}
